import { DomHelpers, on, ViewWidgetHelper, ImSys, Loc, off } from '@nordbox/core';
import { PlayerBaseWidget } from './PlayerBaseWidget.js';
import { PlayerMode } from './IPlayerWidget.js';
import { PlayerServerCommand } from './cmd/PlayerServerCommand.js';
import { PlayerViewCmd } from './PlayerViewCmd.js';

// widgets/player/NFPlayerWidget.ts
var PlayerSize;
(function (PlayerSize) {
    PlayerSize["size0"] = "size-0";
    PlayerSize["size1"] = "size-1";
    PlayerSize["size2"] = "size-2";
    PlayerSize["size3"] = "size-3";
    PlayerSize["size4"] = "size-4";
})(PlayerSize || (PlayerSize = {}));
class NFPlayerWidget extends PlayerBaseWidget {
    constructor() {
        super(...arguments);
        /** Был ли плеер временно «минимизирован» */
        this._minimized = false;
        /** Устойчивые ссылки на обработчики кнопок размеров/FS (для корректной отписки) */
        this._onSize2Click = () => this._changeSize(PlayerSize.size2);
        this._onSize3Click = () => this._changeSize(PlayerSize.size3);
        this._simpleMode = false;
        this._embedMode = false;
        this._staticPosition = false;
        /** Применить класс размера к корневому элементу */
        this._applySize = () => {
            this.element.classList.remove('size-0', 'size-1', 'size-2', 'size-3', 'size-4');
            if (this._playerSize)
                this.element.classList.add(this._playerSize);
        };
        /** Изменить размер или свёрнутый режим */
        this._changeSize = (size, minimized, fullscreen) => {
            // fullscreen — как было
            if (fullscreen === true) {
                this._toggleFullscreen();
                return;
            }
            // NEW: логика сворачивания только когда мы уже не в "static"
            if (typeof minimized === 'boolean' && this._staticPosition === false && !fullscreen) {
                this._minimized = minimized;
                if (this._minimized) {
                    // запомним текущий размер, чтобы потом вернуть
                    this._tempMinimizedSize = (this._playerSize ?? PlayerSize.size2);
                    this._playerSize = PlayerSize.size4;
                }
                else {
                    this._playerSize = this._tempMinimizedSize === PlayerSize.size4
                        ? PlayerSize.size2
                        : (this._tempMinimizedSize ?? PlayerSize.size2);
                }
                this._applySize();
                this._hideMobileScroll();
                if (this._playerSize === PlayerSize.size4)
                    this.pause();
                else
                    this.play();
                return;
            }
            if (size) {
                // NEW: если уходим в size-1 из другого размера — запомним «не-статичный» размер
                if (size === PlayerSize.size1 && this._playerSize && this._playerSize !== PlayerSize.size1) {
                    this._sizeNotStatic = this._playerSize;
                }
                this._playerSize = size;
                this._applySize();
                this._hideMobileScroll();
                return;
            }
            // умолчание (как в старом — если ещё не был задан)
            if (!this._playerSize)
                this._playerSize = PlayerSize.size4;
            this._applySize();
        };
        /** Кнопка «свернуть» (nf-player__rollup) */
        this._toggleRollup = () => {
            this._changeSize(undefined, !this._minimized, false);
        };
        /** На мобилках прятать скролл, когда плеер «поверх» */
        this._hideMobileScroll = () => {
            try {
                if (window.innerWidth < 768) {
                    const html = document.documentElement;
                    const needHide = this._playerSize === PlayerSize.size4;
                    html.style.overflow = needHide ? 'hidden' : 'auto';
                }
            }
            catch { /* no-op */ }
        };
        /** Навесить клики на элементы управления размерами */
        this._wireSizeControls = () => {
            this.element.querySelector('.sizeContainer .size-2')?.addEventListener('click', this._onSize2Click);
            this.element.querySelector('.sizeContainer .size-3')?.addEventListener('click', this._onSize3Click);
            // «свернуть»
            this.element.querySelector('.nf-player__rollup')?.addEventListener('click', this._toggleRollup);
        };
        /** Обработчик клика по внешним «триггерам» воспроизведения */
        this._onExternalTriggerClick = (ev) => {
            const target = ev.currentTarget;
            let el = target;
            if (el.classList.contains('play-area') && !el.hasAttribute('data-footage')) {
                const nested = el.querySelector('[data-footage],[data-film],[data-file]');
                if (nested)
                    el = nested;
                else
                    return;
            }
            DomHelpers.show(this.element);
            // решаем, нужно ли «поднимать» плеер
            const inFilm = !!this.element.closest('.film-detailed');
            const infoEl = document.querySelector('.film-detailed__film-info');
            const pastInfo = infoEl
                ? (window.scrollY > (infoEl.getBoundingClientRect().top + window.scrollY))
                : false;
            // NEW: разворот плеера (как в легаси)
            if (!inFilm || (pastInfo && window.innerWidth >= 768)) {
                this.initChangeSizePlayer();
            }
            // Воспроизведение по атрибутам
            if (el.dataset.footage) {
                const footageID = el.dataset.footage;
                const timeIn = el.dataset.in ? parseFloat(el.dataset.in.replace(',', '.')) : undefined;
                const timeOut = el.dataset.out ? parseFloat(el.dataset.out.replace(',', '.')) : undefined;
                const orderID = el.dataset.o;
                const rowID = el.dataset.r;
                this.playFootage(footageID, timeIn, timeOut, orderID, rowID);
                if (timeIn && timeOut && (timeIn < timeOut)) {
                    this.playSegment(timeIn, timeOut);
                }
                else {
                    this.play();
                }
            }
            else if (el.dataset.film) {
                this.playFilm(el.dataset.film, el.dataset.o, el.dataset.r);
                this.play();
            }
            else if (el.dataset.file) {
                this.playFile(el.dataset.file);
                this.play();
            }
            else {
                return;
            }
            // NEW: скрыть скролл на мобиле (как раньше)
            if (!inFilm) {
                setTimeout(() => this._hideMobileScroll());
            }
            if (window.innerWidth < 768 && inFilm) {
                window.scrollTo({ top: 0, behavior: 'auto' });
            }
        };
        /**
         * Навесить клики по .play-area и [data-footage|data-film|data-file] внутри контейнера.
         * Защищаемся от повторного навешивания через data-nfBound.
         */
        this._bindExternalTriggersIn = (container) => {
            const bind = (node) => {
                const el = node;
                if (el.dataset.nfBound === '1')
                    return;
                if (el === this.element)
                    return;
                el.addEventListener('click', this._onExternalTriggerClick, { passive: true });
                el.dataset.nfBound = '1';
            };
            container.querySelectorAll('.play-area').forEach(bind);
            container.querySelectorAll('[data-footage],[data-film],[data-file]').forEach(bind);
            if (container.classList.contains('play-area')) {
                bind(container);
            }
        };
        /** Инициализация реакции на динамически добавленный HTML (замена легаси onHtmlAdded → onDomInited) */
        this._wireExternalPlayArea = () => {
            this._bindExternalTriggersIn(document.body);
            // Подключаемся к новой шине событий (Events.on/off) и обрабатываем добавленные элементы
            on(document.body, "onDomInited.nfplayer", (ev) => {
                try {
                    const detail = ev.detail || {};
                    // поддерживаем detail.root | detail.elements | detail.target | одиночный элемент
                    const elem = detail.root;
                    if (!elem)
                        return;
                    this._bindExternalTriggersIn(elem);
                }
                catch (err) {
                    console.error('nf-player onDomInited bind error:', err);
                }
            });
        };
        /** Подсветить текущий футаж в плейлисте */
        this._selectCurrentFootageInPlaylist = () => {
            const opts = this.currentModeOptions || {};
            const footageID = opts.footageID;
            if (!footageID)
                return;
            const list = this.element.querySelector('.nf-player__playlist');
            if (!list)
                return;
            // снимаем .current и ставим на нужный элемент
            list.querySelectorAll('.im-item.current').forEach(x => x.classList.remove('current'));
            const curImg = this.element.querySelector(`.nf-player__playlist-image[data-footage="${footageID}"]`);
            const parent = curImg?.closest('.im-item');
            parent?.classList.add('current');
        };
        /** Навесить клики на элементы плейлиста */
        this._wirePlaylistClicks = () => {
            this.element.querySelectorAll('[data-key="playerList"] [data-footage]').forEach(node => {
                node.addEventListener('click', (e) => {
                    const el = e.currentTarget;
                    const footageID = el.dataset.footage;
                    this.playFootage(footageID);
                }, { passive: true });
            });
        };
    }
    static _cssKey() { return "nf-player"; }
    _create(options) {
        options.cfg = options.cfg ?? `view="nf.share.playerController"`;
        super._create({ ...options });
        this._simpleMode = this.element.classList.contains('simple');
        this._embedMode = this.element.classList.contains('embed');
        if (this._simpleMode)
            this._staticPosition = true;
        // Инициализация внешней маски
        this._externalMaskSel = this.element.dataset.extmask;
        this._externalMaskEl = this._externalMaskSel ? document.querySelector(this._externalMaskSel) : null;
        // Кнопка «Сохранить в корзину» (как в легаси .bt-save-cart-tc)
        this.element.addEventListener('click', (ev) => {
            const btn = ev.target.closest('.bt-save-cart-tc');
            if (!btn || !this.element.contains(btn))
                return;
            const i = this.getIn(), o = this.getOut();
            const techInfo = this.element.querySelector('.scene-label')?.value ?? '';
            const opts = this.currentModeOptions || {};
            const footageID = opts.footageID;
            if (!footageID)
                return;
            // Доп. валидация (та же, что и для save)
            this._validateInOutImgAndExecute(this.validateInOut);
            this.saveSceneByFootage({
                footageID,
                timeIn: i,
                timeOut: o,
                techInfo,
                cartMode: true
            });
            this._modeBusy();
        });
        this._wireSizeControls();
        this._wirePlaylistClicks();
        this._selectCurrentFootageInPlaylist();
        if (!this._simpleMode)
            this._wireExternalPlayArea();
        const filmPage = document.querySelector('.film-detailed');
        const infoEl = document.querySelector('.film-detailed__film-info');
        if (filmPage && window.innerWidth >= 768) {
            this._staticPosition = true;
            this._onWindowScroll = () => {
                const top = infoEl ? (infoEl.getBoundingClientRect().top + window.scrollY) : 0;
                if (window.scrollY > top) {
                    this._staticPosition = false;
                    // если плеер ещё «size-1», разворачиваем до прошлого/дефолтного размера
                    if (!['size-2', 'size-3', 'size-4'].includes(this._playerSize ?? '')) {
                        this._changeSize(this._sizeNotStatic ?? PlayerSize.size2);
                    }
                }
                else {
                    this._staticPosition = true;
                    this._changeSize(PlayerSize.size1);
                }
            };
            window.addEventListener('scroll', this._onWindowScroll, { passive: true });
        }
        else if (filmPage && window.innerWidth < 768) {
            this._staticPosition = true; // как в легаси
        }
        // 2026-03-26:
        // Начальный размер нужно задавать через общую логику, иначе _playerSize
        // расходится с DOM-классами и можно получить комбинацию вроде "size-2 size-1".
        if (!this.element.classList.contains('embed')) {
            this._playerSize = PlayerSize.size1;
            this._applySize();
        }
        on(this.element, 'cart-reload', this.onCartReload);
        on(this.element, 'collection-reload', this.onCollectionReload);
    }
    onCartReload() {
        const cart = document.querySelector('.cart');
        if (cart) {
            const view = ViewWidgetHelper.closestOnByName(cart);
            view?.reload();
        }
    }
    onCollectionReload() {
        const col = document.querySelector('.my-collection');
        if (col) {
            const view = ViewWidgetHelper.closestOnByName(col);
            view?.reload();
        }
    }
    onInputChanged() {
        super.onInputChanged();
    }
    // ─────────────────────────────── UI-хелперы (NF) ───────────────────────────────
    /**
     * Установить/снять флаг «без звука» в чекбоксе.
     * @param on true — поставить галку; false — снять
     */
    _setNoSound(on) {
        const cb = this.element.querySelector('.img-container .scene-nosound');
        if (!cb)
            return;
        if (on === undefined) {
            cb.checked = false;
            return;
        }
        cb.checked = !!on;
        this.onInputChanged();
    }
    /**
     * Показать/скрыть блок подписи и записать текст.
     * Если value === undefined → скрыть блок, иначе показать и проставить значение.
     */
    _setLabel(value) {
        const box = this.element.querySelector('.label-container');
        const inp = this._externalMaskEl?.querySelector('.scene-label');
        const inpExt = this.element.querySelector('.scene-label');
        const btns = box?.querySelectorAll('.bt');
        if (!box || !inp || !btns)
            return;
        if (value === undefined) {
            DomHelpers.hide(box);
            inp.value = '';
            btns.forEach((bt) => {
                bt.classList.add('disabled');
            });
        }
        else {
            DomHelpers.show(box);
            inp.value = value;
            if (inpExt)
                inpExt.value = value;
        }
        this.onInputChanged();
    }
    /**
     * Проставить связки с фильмом/частью.
     * Пустые значения → скрыть блоки, как в легаси.
     */
    _setLink(filmID, filmTitle, partTypeID, partNum) {
        const filmInp = this.element.querySelector('[data-key="filmID"]');
        const partTypeInp = this.element.querySelector('[data-key="partsTypeID"]');
        const partNumInp = this.element.querySelector('[data-key="partNum"]');
        const linkBlock = this.element.querySelector('.film-linking');
        const partLinkBlock = this.element.querySelector('.film-part-linking');
        const btnDetails = this.element.querySelector('.bt-filmdetails');
        const has = !!filmID;
        if (filmInp) {
            filmInp.value = has ? (filmTitle ?? '') : '';
            filmInp.dataset.title = has ? (filmTitle ?? '') : '';
            filmInp.dataset.value = has ? String(filmID) : '';
        }
        if (partTypeInp)
            partTypeInp.value = has && partTypeID != null ? String(partTypeID) : '';
        if (partNumInp)
            partNumInp.value = has && partNum != null ? String(partNum) : '';
        if (linkBlock)
            has ? DomHelpers.show(linkBlock) : DomHelpers.hide(linkBlock);
        if (partLinkBlock)
            has ? DomHelpers.show(partLinkBlock) : DomHelpers.hide(partLinkBlock);
        if (btnDetails)
            btnDetails.classList.toggle('disabled', !has);
        this.onInputChanged();
    }
    /**
     * Управление видимостью кнопок «Сохранить фрагмент» / «Добавить в коллекцию/корзину».
     * Если есть валидные orderID/rowID → показываем только сохранение фрагмента.
     */
    _setOrder(orderID, rowID) {
        this._orderID = orderID;
        this._rowID = rowID;
        // Нормализуем: "0" → 0, "" → 0, undefined → 0
        const o = Number(orderID ?? 0);
        const r = Number(rowID ?? 0);
        const hasOrder = Number.isFinite(o) && Number.isFinite(r) && o !== 0 && r !== 0;
        const addToCollection = this.element.querySelector('.add-to-collection');
        const addToCart = this.element.querySelector('.add-to-cart');
        const saveFragment = this.element.querySelector('.save-fragment');
        if (hasOrder) {
            addToCollection && DomHelpers.hide(addToCollection);
            addToCart && DomHelpers.hide(addToCart);
            saveFragment && DomHelpers.show(saveFragment);
            return;
        }
        const inContentCreation = !!this.element.closest('.content-creation');
        if (this.currentMode === PlayerMode.preview
            || this.currentMode === PlayerMode.editFootage
            || this.currentMode === PlayerMode.newFootage) {
            addToCollection && DomHelpers.hide(addToCollection);
            addToCart && DomHelpers.hide(addToCart);
            if (saveFragment) {
                inContentCreation ? DomHelpers.hide(saveFragment) : DomHelpers.show(saveFragment);
            }
        }
        else {
            saveFragment && DomHelpers.hide(saveFragment);
            addToCollection && DomHelpers.show(addToCollection);
            addToCart && DomHelpers.show(addToCart); // CSS должен вернуть display:flex, если нужно
        }
    }
    _onFullscreenStateChanged(isFullscreen) {
        if (isFullscreen) {
            // 2026-03-30: в NF fullscreen — это отдельный layout-режим size-0, как в legacy-плеере.
            this._tempSize = this._playerSize;
            this._playerSize = PlayerSize.size0;
            this._applySize();
        }
        else {
            if (this._staticPosition) {
                // 2026-03-30: при staticPosition сначала возвращаем size-1, чтобы восстановить
                // легаси-поведение привязки к странице, а уже потом возвращаем сохраненный размер.
                this._playerSize = PlayerSize.size1;
                this._applySize();
                this._playerSize = this._tempSize;
            }
            else {
                this._playerSize = this._tempSize ?? PlayerSize.size2;
                this._applySize();
            }
        }
    }
    initChangeSizePlayer() {
        if (this._playerSize === PlayerSize.size1) {
            // 2026-04-13: external click не должен оставлять плеер в static layout size-1.
            this._changeSize(this._sizeNotStatic ?? PlayerSize.size2);
        }
        else if (this._playerSize && this._playerSize !== PlayerSize.size4) {
            this._changeSize(this._playerSize);
        }
        else if (this._tempMinimizedSize) {
            this._changeSize(this._tempMinimizedSize);
        }
        else {
            this._changeSize(PlayerSize.size2);
        }
    }
    // ───────────────────── режимы (добавляем NF-специфику к базе) ─────────────────────
    onBeforePlayerCommand(data) {
        switch (data.type) {
            case PlayerServerCommand.playWebFootage: {
                this.mediaEl.src = data.url;
                this.seek(data.timeIn == undefined ? 0 : data.timeIn);
                if (this.mediaEl.paused)
                    this.engine.play();
                this.chagePlaylist(data.ui);
                this.switchMode(PlayerMode.playWebFootage, data.title, data);
                const btnSimilar = this.element.querySelector('.bt-similar-search');
                const control = new AbortController();
                if (btnSimilar) {
                    if (data.similarSearch) {
                        const onSimilarClick = () => {
                            this._execCmd(PlayerViewCmd.execSimilarSearch, {
                                footageID: data.footageID,
                                currentTime: this.engine.currentTime()
                            });
                        };
                        DomHelpers.show(btnSimilar);
                        btnSimilar.addEventListener('click', onSimilarClick, {
                            signal: control.signal
                        });
                    }
                    else {
                        control.abort();
                        DomHelpers.hide(btnSimilar);
                    }
                }
                break;
            }
            case PlayerServerCommand.sceneInserted:
            case PlayerServerCommand.sceneUpdated:
                // Пришли данные по dto
                this.setInOut();
                this._setLabel();
                this.currentMode = PlayerMode.playWebFootage;
                break;
            case PlayerServerCommand.setFootageParams:
                // Пришли данные по dto
                this.setLink(data.filmID, data.filmTitle, data.partTypeID, data.partNum);
                break;
        }
        return true;
    }
    setLink(filmID, filmTitle, partTypeID, partNum) {
        const film = this.element.querySelector('[data-key=filmID]');
        if (film) {
            film.value = filmID ? filmTitle : '';
            film.dataset.title = filmID ? filmTitle : '';
            film.dataset.value = filmID ? filmID : '';
        }
        const partsType = this.element.querySelector('[data-key=partsTypeID]');
        if (partsType) {
            partsType.value = partTypeID ? partTypeID : '';
        }
        const partNumber = this.element.querySelector('[data-key=partNum]');
        if (partNumber) {
            partNumber.value = partNum ? String(partNum) : '';
        }
        const filmLinking = this.element.querySelector('.film-part-linking, .film-linking');
        if (filmLinking) {
            filmID ? DomHelpers.show(filmLinking) : DomHelpers.hide(filmLinking);
        }
        const filmdetails = this.element.querySelector('.bt-filmdetails');
        if (filmdetails) {
            filmID ? filmdetails.classList.remove('disabled') : filmdetails.classList.add('disabled');
        }
        this.onInputChanged();
    }
    /** Изменить плейлист */
    chagePlaylist(filmID) {
        if (this.element.dataset.film != filmID) {
            const playerList = this.element.querySelector('[data-key=playerList]');
            if (playerList) {
                playerList.innerHTML = '';
            }
            if (!filmID) {
                this.element.dataset.film = '';
            }
            else {
                this.element.dataset.film = filmID;
                const view = ViewWidgetHelper.closestOnByName(playerList);
                if (view) {
                    view.reload('playerList', () => {
                        this._selectCurrentFootageInPlaylist();
                        this.element.querySelectorAll('[data-key=playerList] [data-footage]').forEach((elem) => {
                            elem.addEventListener('click', () => {
                                const footage = elem.dataset.footage;
                                if (footage)
                                    this.playFootage(footage);
                            });
                        });
                    }, undefined, undefined, { filmUI: filmID });
                }
            }
        }
    }
    _hideEls() {
        this.els.tkContainer && DomHelpers.hide(this.els.tkContainer);
        this.els.sceneContainer && DomHelpers.hide(this.els.sceneContainer);
        this.els.playProgress && DomHelpers.hide(this.els.playProgress);
        this.els.imgContainer && DomHelpers.hide(this.els.imgContainer);
        this.els.labelContainer && DomHelpers.hide(this.els.labelContainer);
        this.els.filmPartLinking && DomHelpers.hide(this.els.filmPartLinking);
        this.els.filmLinking && DomHelpers.hide(this.els.filmLinking);
    }
    _disableSlidersProgres(disable = true) {
        if (disable) {
            this.els.playSlider?.setAttribute('disabled', '');
            this.els.volumeSlider?.setAttribute('disabled', '');
            this.els.sceneRange?.setAttribute('disabled', '');
            this.els.playProgress?.classList.add('disabled');
            this.els.loadProgress?.classList.add('disabled');
        }
        else {
            this.els.playSlider?.removeAttribute('disabled');
            this.els.volumeSlider?.removeAttribute('disabled');
            this.els.sceneRange?.removeAttribute('disabled');
            this.els.playProgress?.classList.remove('disabled');
            this.els.loadProgress?.classList.remove('disabled');
        }
    }
    /**
     * Переключение режима с NF-визуальными правками.
     * Ничего нового: строго повторяем поведение старого кода.
     */
    switchMode(mode, title, options) {
        this.currentMode = mode;
        if (options)
            this.currentModeOptions = options;
        const op = options ?? this.currentModeOptions ?? {};
        this._selectCurrentFootageInPlaylist();
        switch (mode) {
            case PlayerMode.off:
                if (!title)
                    title = '';
                if (this.mediaEl.src) {
                    this.engine.pause();
                    this.mediaEl.currentTime = 0;
                }
                this._hideEls();
                this._disableSlidersProgres(true);
                this.setInOut();
                this.setImg();
                this._setLink();
                this._setLabel();
                this._setOrder();
                break;
            case PlayerMode.playWebFootage: {
                // диапазон/метки
                if (op.timeOut > 0)
                    this.setInOut(op.timeIn, op.timeOut);
                else
                    this.setInOut();
                this.setImg();
                this._setLink();
                this._setLabel(op.timeOut > 0 ? (op.techInfo ?? '') : undefined);
                this._setOrder(op.orderID, op.rowID);
                if (op.timeIn >= 0)
                    this.seek(op.timeIn);
                break;
            }
            case PlayerMode.newScene:
                this._setLabel('');
                this._setOrder();
                break;
            case PlayerMode.preview:
                this.setInOut();
                this.setImg();
                this._setLink();
                this._setLabel();
                this._setOrder();
                break;
            case PlayerMode.editFootage:
                this.setInOut(op.timeIn, op.timeOut, true);
                this.setImg(op.titleImgTime);
                this._setNoSound(op.noSound);
                this._setLabel(op.techInfo);
                this._setLink(op.filmID, op.filmTitle, op.partTypeID, op.partNum);
                this.seek(op.timeIn);
                this._setOrder();
                break;
            case PlayerMode.newFootage:
                // показать блок картинки и пустую подпись
                this.setImg();
                this._setLabel();
                this._setOrder();
                break;
            case PlayerMode.audio:
                this.setInOut();
                this.setImg();
                this._setLink();
                this._setLabel();
                this._setOrder();
                break;
        }
        if (title !== undefined)
            this._setTitle(title);
    }
    // ───────────────────────────── сохранение (как было) ─────────────────────────────
    _onSaveTcRequested() {
        const i = this.getIn();
        const o = this.getOut();
        const img = this.getImg();
        const noSound = !!this.element.querySelector(".img-container .scene-nosound")?.checked;
        const labelInput = this.element.querySelector(".scene-label");
        const techInfo = labelInput?.value ?? "";
        const duration = this.mediaEl.duration || Math.max(0, o - i);
        const opts = this.currentModeOptions || {};
        const fileID = opts.fileID;
        const footageID = opts.footageID;
        switch (this.currentMode) {
            case PlayerMode.editFootage:
                this.saveChangedFootage({
                    footageID, timeIn: i, timeOut: o, titleImgTime: img,
                    noSound, techInfo
                });
                break;
            case PlayerMode.newFootage:
                this.saveNewFootage({
                    fileID, timeIn: i, timeOut: o, titleImgTime: img,
                    noSound, techInfo, duration
                });
                break;
            case PlayerMode.newScene:
            case PlayerMode.playWebFootage:
                this.saveSceneByFootage({
                    footageID, timeIn: i, timeOut: o, techInfo
                });
                break;
            default:
                ImSys.alert(Loc.savingError, Loc.unknownEditMode);
                return;
        }
        /*this.element.querySelector(".bt-save-tc")?.classList.add("disabled");*/
    }
    // ───────────────────────────── lifecycle ─────────────────────────────
    _onDestroy() {
        // отписка от шины событий по namespace
        off(document.body, ".nfplayer");
        off(this.element, 'cart-reload');
        off(this.element, 'collection-reload');
        // клики размеров/свёртывания
        this.element.querySelector('.size-2')?.removeEventListener('click', this._onSize2Click);
        this.element.querySelector('.size-3')?.removeEventListener('click', this._onSize3Click);
        this.element.querySelector('.nf-player__rollup')?.removeEventListener('click', this._toggleRollup);
        // внешние триггеры внутри нашего DOM (снимем точечно)
        this.element.querySelectorAll('.play-area,[data-footage],[data-film],[data-file]').forEach(el => {
            if (el.dataset.nfBound === '1') {
                el.removeEventListener('click', this._onExternalTriggerClick);
                delete el.dataset.nfBound;
            }
        });
        if (this._onWindowScroll) {
            window.removeEventListener('scroll', this._onWindowScroll);
            this._onWindowScroll = undefined;
        }
        super._onDestroy();
    }
}
NFPlayerWidget._alias = "NFPlayer";

export { NFPlayerWidget, PlayerSize };
